import { CommonModule, NgStyle } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';
import { GlobalFilterService } from '../../../../my-shared-lib/src/lib/shared-services/global-filter-service';
import { GlobalService } from '../../../../my-shared-lib/src/lib/shared-services/global.service';
import { Subscription } from 'rxjs';
import { formatStat } from '../../../../my-shared-lib/src/lib/utils/format.utils';
import { MatIconModule } from '@angular/material/icon';

const FILTER_KEY_ALIASES: Record<string, string> = {
  total_investment_range: 'simulation_investment_estimate',
  project_score: 'simulation_final_score',
};
const RANGE_FILTER_KEYS = new Set(['simulation_investment_estimate', 'simulation_final_score']);

@Component({
  selector: 'app-number-stats',
  standalone: true,
  imports: [NgStyle, CommonModule, MatIconModule],
  templateUrl: './number-stats.component.html',
  styleUrl: './number-stats.component.scss'
})
export class NumberStatsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  stat!: string;
  private filterSub!: Subscription;
  private globalServiceSub!: Subscription;

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private globalService: GlobalService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data?.value !== undefined && this.data?.value !== null) {
      const rawValue = this.resolveValue(this.data.value);
      this.stat = this.formatDisplayValue(rawValue);
    }
  }

  ngAfterViewInit(): void {
    this.loadStat(this.mergeActiveFilters()); // load initially with active filters

    this.filterSub = this.globalFilterService.filter$.subscribe(filters => {
      if (Object.keys(filters).length !== 0) {
        this.loadStat(this.mergeActiveFilters(filters));
      }
    });

    this.globalServiceSub = this.globalService.filters$.subscribe((filters) => {
      this.loadStat(this.mergeActiveFilters(filters));
    });
  }
  get formattedTitle(): string {
    return this.data?.title?.replace(/\n/g, '<br>') || '';
  }


  loadStat(filters?: any): void {
    if (this.data?.value !== undefined && this.data?.value !== null) {
      const rawValue = this.resolveValue(this.data.value);
      this.stat = this.formatDisplayValue(rawValue);
      return;
    }

    const normalizedFilters = this.extractActiveFilters(filters);
    const useRequestPayload = !!this.data?.requestPayload;

    const request$ = useRequestPayload
      ? this.configService.customApi(
        this.data?.apiUrl,
        this.buildRequestPayload(normalizedFilters),
        this.data,
        this.data?.method || 'post',
      )
      : this.configService.getConfig(this.data?.apiUrl, {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false
      }, this.data?.method || 'get');

    request$?.subscribe({
      next: (res: any) => {
        const responseRoot = useRequestPayload ? this.resolveResponseData(res) : res;
        const summaryRecord = this.resolveSummaryRecord(responseRoot);
        const parent = this.data?.parentKey ? summaryRecord?.[this.data?.parentKey] : summaryRecord;
        const rawValue = this.byPath(parent, this.data?.key) ?? parent?.[this.data.key];

        const resolvedValue = this.resolveValue(rawValue);

        this.stat = this.formatDisplayValue(resolvedValue);
      },
      error: (err) => {
        console.error("Error loading stat:", err);
      }
    });
  }
  private resolveValue(rawValue: any): number | any {
    if (this.data?.integerOnly) {
      const numeric = Number(rawValue);
      if (!Number.isNaN(numeric)) {
        return Math.round(numeric);
      }
    }
    return rawValue;
  }

  private formatDisplayValue(rawValue: any): string {
    if (this.data?.integerOnly) {
      const numeric = Number(rawValue);
      if (!Number.isNaN(numeric)) {
        const rounded = Math.round(numeric);
        const prefix = this.data?.prefix || '';
        return `${prefix}${rounded}`;
      }
    }

    const decimalPlaces = Number(this.data?.decimalPlaces);
    if (
      Number.isInteger(decimalPlaces) &&
      decimalPlaces >= 0 &&
      rawValue !== null &&
      rawValue !== undefined &&
      rawValue !== ''
    ) {
      const numeric = Number(rawValue);
      if (!Number.isNaN(numeric)) {
        const prefix = this.data?.prefix || '';
        return `${prefix}${numeric.toFixed(decimalPlaces)}`;
      }
    }

    if (this.data?.useFormatStat) {
      return formatStat(rawValue, this.data?.key || '', this.data?.prefix || '');
    }

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }

    const prefix = this.data?.prefix || '';
    return `${prefix}${rawValue}`;
  }

  private extractActiveFilters(filters: any): Record<string, unknown> {
    if (
      (filters?.requestParams && typeof filters.requestParams === 'object') ||
      (filters?.queryParams && typeof filters.queryParams === 'object')
    ) {
      return {
        ...((filters?.requestParams || {}) as Record<string, unknown>),
        ...((filters?.queryParams || {}) as Record<string, unknown>),
      };
    }
    if (filters?.filter && typeof filters.filter === 'object') {
      return { ...filters.filter };
    }
    return filters && typeof filters === 'object' ? { ...filters } : {};
  }

  private mergeActiveFilters(triggerFilters?: any): Record<string, unknown> {
    const fromGlobalService = this.extractActiveFilters(this.globalService.getCurrentFilters());
    const fromGlobalFilterService = this.extractActiveFilters(this.globalFilterService.getCurrentFilters());
    const fromTrigger = this.extractActiveFilters(triggerFilters);

    return {
      ...fromGlobalService,
      ...fromGlobalFilterService,
      ...fromTrigger,
    };
  }

  private buildRequestPayload(filters?: Record<string, unknown>): Record<string, unknown> {
    const requestPayload = this.data?.requestPayload;
    if (!requestPayload || typeof requestPayload !== 'object') {
      return {};
    }

    const clonedPayload = JSON.parse(JSON.stringify(requestPayload)) as Record<string, unknown>;

    if (!this.data?.mergeActiveFilters || !filters || !Object.keys(filters).length) {
      return clonedPayload;
    }

    const requestData = (clonedPayload['requestData'] ?? {}) as Record<string, unknown>;
    const payloadFilter = (requestData['filter'] ?? {}) as Record<string, unknown>;
    const normalizedFilters = this.remapFilterKeys(filters);

    Object.entries(normalizedFilters).forEach(([key, rawValue]) => {
      const payloadValue = this.normalizeFilterPayloadValue(key, rawValue);
      if (payloadValue) {
        payloadFilter[key] = payloadValue;
      }
    });

    requestData['filter'] = payloadFilter;
    clonedPayload['requestData'] = requestData;
    return clonedPayload;
  }

  private resolveResponseData(response: unknown): unknown {
    const configuredPath = this.data?.responsePath;
    if (typeof configuredPath === 'string' && configuredPath.trim()) {
      const resolved = this.byPath(response, configuredPath.trim());
      if (resolved !== undefined) {
        return resolved;
      }
    }

    const roadmapCandidates = [
      'responseList.0.simulation_roadmap.0',
      'responseList.0.simulation_roadmap',
      'responseList.0.responseData.0.simulation_roadmap.0',
      'responseList.0.responseData.0.simulation_roadmap',
      'responseData.0.simulation_roadmap.0',
      'responseData.0.simulation_roadmap',
      'simulation_roadmap.0',
      'simulation_roadmap',
    ];

    for (const path of roadmapCandidates) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    const fallbacks = [
      'responseList.0.responseData.0',
      'responseList.0.responseData',
      'responseData.0',
      'responseData',
      'data',
    ];

    for (const path of fallbacks) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    return response;
  }

  private resolveSummaryRecord(source: unknown): any {
    if (Array.isArray(source)) {
      const summaryIndex = Number(this.data?.summaryIndex);
      if (Number.isInteger(summaryIndex) && summaryIndex >= 0 && summaryIndex < source.length) {
        return source[summaryIndex];
      }

      const summaryKey = typeof this.data?.summaryKey === 'string' ? this.data.summaryKey : '';
      if (summaryKey) {
        const matched = source.find(
          (item) => item && typeof item === 'object' && this.byPath(item, summaryKey) !== undefined,
        );
        if (matched) {
          return matched;
        }
      }
      return source[0];
    }
    return source;
  }

  private remapFilterKeys(filters: Record<string, unknown>): Record<string, unknown> {
    const remapped: Record<string, unknown> = {};
    Object.entries(filters || {}).forEach(([key, value]) => {
      const targetKey = FILTER_KEY_ALIASES[key] || key;
      remapped[targetKey] = value;
    });
    return remapped;
  }

  private normalizeFilterPayloadValue(key: string, value: unknown): unknown[][] | null {
    const tokens = this.normalizeFilterValues(value);
    if (!tokens.length) {
      return null;
    }

    if (RANGE_FILTER_KEYS.has(key) && tokens.length >= 2) {
      const first = Number(tokens[0]);
      const second = Number(tokens[1]);
      if (Number.isFinite(first) && Number.isFinite(second)) {
        return [[first], [second]];
      }
    }

    return [tokens];
  }

  private normalizeFilterValues(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      const normalized = value
        .flat(Infinity)
        .map((item) => (typeof item === 'string' ? item.trim() : item))
        .filter((item) => item !== null && item !== undefined && item !== '');

      const unique: unknown[] = [];
      const seen = new Set<string>();
      normalized.forEach((item) => {
        const signature = `${typeof item}:${String(item)}`;
        if (!seen.has(signature)) {
          seen.add(signature);
          unique.push(item);
        }
      });
      return unique;
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    if (typeof value === 'string') {
      const token = value.trim();
      return token ? [token] : [];
    }
    return [value];
  }

  private byPath(source: any, path: string): any {
    return String(path)
      .split('.')
      .reduce(
        (current: any, key: string) =>
          current == null ? undefined : /^\d+$/.test(key) ? current[Number(key)] : current[key],
        source,
      );
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.globalServiceSub?.unsubscribe();
  }
}
